import React, { useState } from 'react';
import { Box, TextField, IconButton, Tooltip, SxProps, Theme, Typography, Stack } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';

interface LinkBoxProps {
    link: string;
    placeholder?: string;
    sx?: SxProps<Theme>;
    onCopySuccess?: () => void;
    onCopyError?: (error: Error) => void;
    customComponent?: JSX.Element;
}

const LinkBox: React.FC<LinkBoxProps> = ({
    link = 'https://example.com/shared-document',
    placeholder = 'Link will appear here...',
    sx = {},
    onCopySuccess,
    onCopyError,
    customComponent,
}) => {
    const [copied, setCopied] = useState(false);
    const { themeStyle } = useThemeContext();

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            onCopySuccess?.();

            // Reset the copied state after 2 seconds
            setTimeout(() => {
                setCopied(false);
            }, 2000);
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to copy link');
            onCopyError?.(error);
        }
    };

    return (
        <Box
            sx={{
                position: 'relative',
                width: '100%',
                border: `1px solid ${theme[themeStyle].gray_message}`,
                p: 2,
                borderRadius: 2,
                my: 1,
                ...sx,
            }}>
            <Stack pb={2} direction={'row'} justifyContent={'space-between'}>
                <Stack>
                    <Typography
                        sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, color: theme[themeStyle].tag_white, fontSize: 16 }}>
                        Share Link
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            color: theme[themeStyle].darkGray,
                            fontSize: 12,
                        }}>
                        Copy the link below to share with others
                    </Typography>
                </Stack>
                {customComponent && customComponent}
            </Stack>
            <TextField
                value={link}
                placeholder={placeholder}
                InputProps={{
                    readOnly: true,
                    endAdornment: (
                        <Tooltip title={copied ? 'Copied!' : 'Copy link'}>
                            <IconButton
                                onClick={handleCopy}
                                disabled={!link}
                                size="small"
                                sx={{
                                    color: copied ? theme[themeStyle].primaryGreen : theme[themeStyle].placeholderGray,
                                    '&:hover': {
                                        bgcolor: theme[themeStyle].button_background,
                                        color: copied ? theme[themeStyle].primaryGreen : theme[themeStyle].true_white,
                                    },
                                    '&:disabled': {
                                        color: theme[themeStyle].placeholderGray,
                                    },
                                    transition: 'all 0.2s ease-in-out',
                                    padding: 1,
                                }}>
                                {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
                            </IconButton>
                        </Tooltip>
                    ),
                    sx: {
                        bgcolor: theme[themeStyle].form_fields,
                        color: theme[themeStyle].darkGray,
                        '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme[themeStyle].form_divider,
                        },
                        '&:hover .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme[themeStyle].highlight,
                        },
                        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                            borderColor: theme[themeStyle].highlight,
                        },
                        '& .MuiInputBase-input': {
                            color: theme[themeStyle].darkGray,
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: 14,
                            paddingRight: '40px', // Make room for the icon
                            '&::placeholder': {
                                color: theme[themeStyle].placeholderGray,
                                opacity: 1,
                            },
                        },
                    },
                }}
                sx={{
                    width: '100%',
                    '& .MuiInputBase-root': {
                        borderRadius: 2,
                    },
                }}
            />
        </Box>
    );
};

export { LinkBox };
